// Item selection rules for "New invoice from work orders" (INVOICE-BY-ITEM.md §2–§3).
// Work orders: [{ id, number, items: [{ id, lineTotal, ready, invoice?, cancelled? }] }]; selection: Set of item ids.

const billable = (wo) => (wo.items || []).filter((i) => !i.cancelled);
const readyItems = (wo) => billable(wo).filter((i) => i.ready && !i.invoice);

export const initialSelection = (workOrders) => new Set(workOrders.flatMap((wo) => readyItems(wo).map((i) => i.id)));

export const toggleItem = (selection, item) => {
  const next = new Set(selection);
  if (next.has(item.id)) next.delete(item.id);
  else if (item.ready && !item.invoice) next.add(item.id);
  return next;
};

// 'all' | 'some' | 'none' over the work order's ready items.
export const workOrderCheckState = (wo, selection) => {
  const ready = readyItems(wo);
  const picked = ready.filter((i) => selection.has(i.id)).length;
  if (picked === 0) return 'none';
  return picked === ready.length ? 'all' : 'some';
};

export const toggleWorkOrder = (selection, wo) => {
  const next = new Set(selection);
  const ready = readyItems(wo);
  const selectAll = workOrderCheckState(wo, selection) !== 'all';
  ready.forEach((i) => (selectAll ? next.add(i.id) : next.delete(i.id)));
  return next;
};

// wholeWorkOrdersOnly: every work order touched has all its billable items selected — the case today's backend
// (whole-work-order invoicing) can already create.
export const selectionSummary = (workOrders, selection) => {
  let count = 0;
  let total = 0;
  const workOrderIds = [];
  let wholeWorkOrdersOnly = true;
  workOrders.forEach((wo) => {
    const items = billable(wo);
    const picked = items.filter((i) => selection.has(i.id));
    if (picked.length === 0) return;
    workOrderIds.push(wo.id);
    count += picked.length;
    total += picked.reduce((s, i) => s + Number(i.lineTotal || 0), 0);
    if (picked.length !== items.length) wholeWorkOrdersOnly = false;
  });
  return { count, total: Math.round(total * 100) / 100, workOrderIds, wholeWorkOrdersOnly };
};

// NOT_INVOICED | PARTLY_INVOICED | INVOICED (cancelled items don't count).
export const billingStateOf = (wo) => {
  const items = billable(wo);
  const invoiced = items.filter((i) => i.invoice).length;
  if (invoiced === 0) return 'NOT_INVOICED';
  return invoiced === items.length ? 'INVOICED' : 'PARTLY_INVOICED';
};
